/**
 * ONE RULE, THREE CALLERS (T5.17) — this is the browser's copy of `backend/services/did.py`'s
 * `did_from_address`, and `tests/test_did_address_prefill.py` COMPILES AND RUNS THIS FILE
 * against the Python function's answers on a shared case table. It is not two rules that
 * happen to look alike: a divergence is a test failure, not a subtle behaviour difference
 * discovered in production.
 *
 * Our convention is `<DID>@e2e.ccs.biz` in BOTH directions — the inbound mailbox is named for
 * the DID that receives, the outbound `From` for the DID that sends — so the same derivation
 * serves the IMAP username, the SMTP From address, and this pre-fill.
 *
 * THE REFUSAL IS THE FEATURE. `faxes@e2e.ccs.biz`, `ccs-inbound@…` and `fax+3105551212@…` all
 * return null: the local part must be digits and nothing else. Digging the digits out of a
 * named mailbox would pre-fill a number the operator never configured — and a pre-filled
 * field is one an operator trusts without re-reading, so a wrong guess here is worse than no
 * guess. Every fax for that job would then be filed under a DID nobody chose.
 *
 * The value is returned AS ENTERED, so an eleven-digit address keeps its country code:
 * `+13105551212`, `13105551212` and `310-555-1212` are the same number and none is wrong, and
 * the server compares them on the last ten digits rather than reformatting what was typed.
 *
 * IT IS A PRE-FILL, NEVER AN OVERWRITE. See `didPrefillFromAddress`.
 */

/** How many trailing digits a DID comparison uses. `did.py`'s `DID_COMPARE_DIGITS`. */
export const DID_COMPARE_DIGITS = 10

/**
 * The one inbound transport whose destination DID a mailbox implies (4c).
 *
 * The browser's copy of `backend/services/job_did_prefill.DESTINATION_DID_TRANSPORT`, which is
 * itself read from `account_did_derivation.DERIVED_DID_TRANSPORT[inbound]` rather than written as a
 * literal. `tests/test_did_address_prefill.py` compares the two across the runtimes, so a change on
 * either side is a test failure rather than a form that demands a DID the API would have derived.
 *
 * WHY THE BROWSER NEEDS IT AT ALL: the job form must not block a save on a value the server fills
 * in. An IMAP inbound leg reads a `mail` account whose mailbox address IS the destination DID, so
 * demanding it client-side would make the form the last surface still asking for it — and would
 * refuse before the API ever got the chance to derive it.
 */
export const DERIVED_DESTINATION_DID_TRANSPORT = 'imap'

/**
 * The shortest and longest digit strings accepted as a DID. Ten is a bare NANP number, eleven
 * is the same number with its country code. NINE IS NOT A DID AND TWELVE IS NOT EITHER: a
 * mailbox named for a ticket number, an extension or a date would otherwise read as a phone
 * number, which is the coercion this refuses. Closed on both ends for that reason.
 */
export const ADDRESS_DID_MIN_DIGITS = 10
export const ADDRESS_DID_MAX_DIGITS = 11

const ALL_DIGITS = /^\d+$/

/**
 * Unwrap an RFC-5322 display form (`Fax Line <13105551212@e2e.ccs.biz>`) to the bare address.
 *
 * `from_email` legitimately holds one and the DID is no less derivable for it. Mirrors what
 * Python's `email.utils.parseaddr` does for the cases this rule can accept — a local part that
 * is anything other than plain digits is refused below regardless, so the quoting and comment
 * syntax `parseaddr` also handles cannot change an answer here.
 */
function bareAddress(value: string): string {
  const trimmed = value.trim()
  const open = trimmed.lastIndexOf('<')
  const close = trimmed.lastIndexOf('>')
  if (open !== -1 && close > open) {
    return trimmed.slice(open + 1, close).trim()
  }
  return trimmed
}

/**
 * The DID in an address's local part, AS ENTERED, or null if it is not one.
 *
 * Behaviourally identical to `backend/services/did.py::did_from_address`; see the file header.
 */
export function didFromAddress(address: string | null | undefined): string | null {
  if (!address) return null
  const addr = bareAddress(address)
  if (!addr || !addr.includes('@')) return null
  const at = addr.lastIndexOf('@')
  const local = addr.slice(0, at).trim()
  const domain = addr.slice(at + 1)
  if (!local || !domain) return null
  if (!ALL_DIGITS.test(local)) return null
  if (local.length < ADDRESS_DID_MIN_DIGITS || local.length > ADDRESS_DID_MAX_DIGITS) {
    return null
  }
  return local
}

/**
 * What the Destination DID field should become when an address is entered — or `current`
 * unchanged.
 *
 * A PRE-FILL AND NEVER AN OVERWRITE. A DID the operator has already typed is the authoritative
 * one (design §2.5 keeps `destination_did` authoritative even against a disagreeing address),
 * so replacing it with a derivation would silently retarget a job whose number was deliberate —
 * and the operator would have no way to tell it happened. A disagreement between the two is a
 * finding the server records, not something this function resolves.
 */
export function didPrefillFromAddress(
  current: string | null | undefined,
  address: string | null | undefined,
): string {
  const existing = (current ?? '').trim()
  if (existing) return current ?? ''
  return didFromAddress(address) ?? (current ?? '')
}
